import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { FileDown, Mail, Loader2, ExternalLink, Info, Eye, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthToken } from '../lib/authToken';
import { saveBlob } from '../lib/download';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => { const [y, m, j] = d.split('-'); return `${j}/${m}/${y}`; };

export const PresencesExport = ({ dossierId, date, onBeforeExport, canEdit }) => {
  const [loading, setLoading] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [synthese, setSynthese] = useState(null);
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const base = `${API_URL}/portail/formateur/journee/${dossierId}/${date}/synthese-presences`;
  const headers = { Authorization: `Bearer ${getAuthToken()}` };

  const prepare = async () => { if (canEdit && onBeforeExport) await onBeforeExport(); };

  const downloadPdf = async () => {
    setLoading('pdf');
    try {
      await prepare();
      const res = await axios.get(`${base}.pdf`, { headers, responseType: 'blob' });
      saveBlob(res.data, `presences_${date}.pdf`);
      toast.success('PDF téléchargé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la génération du PDF'); }
    finally { setLoading(null); }
  };

  const openMail = async () => {
    setLoading('mail');
    try {
      await prepare();
      const res = await axios.get(base, { headers });
      setSynthese(res.data);
      setSelected(res.data.destinataires.map(d => d.email));
      setMailOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de chargement'); }
    finally { setLoading(null); }
  };

  const buildText = (s, withGreeting = true) => {
    const lines = [];
    if (withGreeting) lines.push('Bonjour,', '', `Veuillez trouver ci-joint la synthèse des présences de la journée du ${fmtDate(date)}.`, '');
    else lines.push(`SYNTHÈSE DES PRÉSENCES – Journée du ${fmtDate(date)}`, '');
    lines.push(
      `Groupe : ${s.groupe || ''}`,
      `Formation : ${s.formation || ''}`,
      `Centre : ${s.centre || ''}`,
      `Formateur : ${s.formateur || ''}`,
      `Réf. groupe : ${s.ref_groupe || ''}`,
      `Module du jour : ${s.module_du_jour || ''}`,
      '',
      `Présents : ${s.stats.presents} / ${s.stats.total} (dont ${s.stats.retards} en retard) – Absents : ${s.stats.absents} – Taux de présence : ${s.stats.taux} %`,
      '',
      'Apprenants :',
      ...s.rows.map(r => `- ${r.apprenant} : ${r.statut}${r.motif ? ` – Motif : ${r.motif}` : ''}${r.prevenu ? ` – Prévenu : ${r.prevenu}` : ''}${r.justifie ? ` – Justifié : ${r.justifie}` : ''}`),
    );
    if (withGreeting) lines.push('', 'Cordialement,', s.formateur || '');
    return lines.join('\n');
  };

  const buildMailto = () => {
    const extras = extra.split(/[;,\s]+/).map(s => s.trim()).filter(Boolean);
    const to = [...new Set([...selected, ...extras])].join(',');
    const subject = `Synthèse des présences du ${fmtDate(date)} – ${synthese.groupe || synthese.ref_groupe}`;
    return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildText(synthese))}`;
  };

  const openPreview = async () => {
    setLoading('preview');
    try {
      await prepare();
      const res = await axios.get(base, { headers });
      setSynthese(res.data);
      setPreviewOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de chargement'); }
    finally { setLoading(null); }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(buildText(synthese, false));
      setCopied(true);
      toast.success('Compte rendu copié');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copie impossible : sélectionnez le texte manuellement'); }
  };

  const handleSend = async () => {
    await downloadPdf();
    window.location.href = buildMailto();
    setMailOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-2" data-testid="presences-export">
        <Button type="button" variant="outline" size="sm" className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={openPreview} disabled={!!loading} data-testid="btn-presences-preview">
          {loading === 'preview' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />} Aperçu
        </Button>
        <Button type="button" variant="outline" size="sm" className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={downloadPdf} disabled={!!loading} data-testid="btn-presences-pdf">
          {loading === 'pdf' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />} PDF
        </Button>
        <Button type="button" size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={openMail} disabled={!!loading} data-testid="btn-presences-mail">
          {loading === 'mail' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />} Envoyer par email
        </Button>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="presences-preview-dialog">
          <DialogHeader>
            <DialogTitle>Aperçu du compte rendu de présences</DialogTitle>
            <DialogDescription>Journée du {fmtDate(date)} · {synthese?.groupe}</DialogDescription>
          </DialogHeader>
          {synthese && (
            <div className="space-y-4" data-testid="presences-preview-content">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                {[['Groupe', synthese.groupe], ['Formation', synthese.formation], ['Centre', synthese.centre], ['Formateur', synthese.formateur], ['Réf. groupe', synthese.ref_groupe], ['Module du jour', synthese.module_du_jour]].map(([k, v]) => (
                  <div key={k} className="flex gap-2"><span className="text-slate-500 w-28 shrink-0">{k}</span><span className="font-medium text-slate-800">{v || '—'}</span></div>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[[`${synthese.stats.presents} / ${synthese.stats.total}`, 'Présents (retards inclus)', 'bg-emerald-50 border-emerald-200'], [synthese.stats.retards, 'Retards', 'bg-amber-50 border-amber-200'], [synthese.stats.absents, 'Absents', 'bg-red-50 border-red-200'], [`${synthese.stats.taux} %`, 'Taux de présence', 'bg-indigo-50 border-indigo-200']].map(([v, l, cls]) => (
                  <div key={l} className={`rounded-lg border p-2 ${cls}`}><p className="text-lg font-bold text-slate-800">{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
                ))}
              </div>
              <table className="w-full text-xs border">
                <thead className="bg-slate-100">
                  <tr>{['Apprenant', 'Statut', 'Motif', 'Prévenu', 'Justifié', 'Décision OF'].map(h => <th key={h} className="px-2 py-1.5 text-left font-semibold text-slate-600 border-b">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {synthese.rows.map(r => (
                    <tr key={r.candidat_id} className="border-b last:border-b-0">
                      <td className="px-2 py-1.5 font-medium text-slate-800">{r.apprenant}</td>
                      <td className={`px-2 py-1.5 ${r.statut_code === 'absent' ? 'text-red-700' : r.statut_code === 'retard' ? 'text-amber-700' : r.statut_code === 'en_aft' ? 'text-cyan-700' : 'text-emerald-700'}`}>{r.statut}</td>
                      <td className="px-2 py-1.5 text-slate-700">{r.motif}</td>
                      <td className="px-2 py-1.5 text-slate-700">{r.prevenu}</td>
                      <td className="px-2 py-1.5 text-slate-700">{r.justifie}</td>
                      <td className="px-2 py-1.5 text-slate-300"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div>
                <Label className="text-xs">Version texte (à copier-coller)</Label>
                <pre className="mt-1 whitespace-pre-wrap text-xs bg-slate-50 border rounded-md p-3 font-sans text-slate-700 select-all" data-testid="presences-preview-text">{buildText(synthese, false)}</pre>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={copyText} disabled={!synthese} data-testid="btn-presences-copy">
              {copied ? <Check className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />} Copier le texte
            </Button>
            <Button type="button" variant="outline" onClick={downloadPdf} disabled={!!loading}><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
            <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setPreviewOpen(false); openMail(); }}><Mail className="w-4 h-4 mr-1" /> Envoyer par email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-lg" data-testid="presences-mail-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer la synthèse des présences</DialogTitle>
            <DialogDescription>Journée du {fmtDate(date)} · {synthese?.groupe}</DialogDescription>
          </DialogHeader>
          {synthese && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">Destinataires (contacts du client / financeur)</Label>
                {synthese.destinataires.length === 0 ? (
                  <p className="text-xs text-slate-400 italic" data-testid="no-destinataires">Aucun contact avec email sur le client ou les financeurs de cette action.</p>
                ) : synthese.destinataires.map(d => (
                  <label key={d.email} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`dest-${d.email}`}>
                    <input type="checkbox" checked={selected.includes(d.email)} onChange={(e) => setSelected(e.target.checked ? [...selected, d.email] : selected.filter(x => x !== d.email))} className="rounded" />
                    <span className="font-medium text-slate-800">{d.nom || d.email}</span>
                    <span className="text-xs text-slate-500">{d.email}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{d.source}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Adresses supplémentaires (séparées par des virgules)</Label>
                <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="ex: contact@entreprise.fr, rh@entreprise.fr" className="h-8 text-sm" data-testid="presences-mail-extra" />
              </div>
              <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border rounded-md p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-indigo-500" />
                <span>Le PDF sera téléchargé puis votre messagerie s'ouvrira avec les destinataires et le texte préremplis : il vous restera à <strong>joindre le PDF</strong> téléchargé.</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMailOpen(false)}>Annuler</Button>
            <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSend} disabled={!synthese || (selected.length === 0 && !extra.trim())} data-testid="presences-mail-send">
              <ExternalLink className="w-4 h-4 mr-1" /> Ouvrir ma messagerie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
